import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Warehouse, Plus, Trash2, Pencil, Search, Loader2, X, MapPin, ArrowDownUp, AlertTriangle, ArrowUp, ArrowDown } from "lucide-react";

const inputCls = "w-full h-11 rounded-lg bg-slate-900 border border-slate-700 px-4 text-sm outline-none focus:border-amber-500 transition-colors";
const empty = { name: "", codice_azienda: "", codice_prodotto: "", ubicazione: "", quantity: "", reorder_threshold: "", supplier_id: "", notes: "" };

function MovementsDialog({ item, onClose, onChanged }) {
  const [movements, setMovements] = useState([]);
  const [type, setType] = useState("carico");
  const [quantity, setQuantity] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/warehouse/${item.id}/movements`).then((r) => setMovements(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [item.id]);

  const submit = async (e) => {
    e.preventDefault();
    const q = Number(quantity);
    if (!q || q <= 0) return toast.error("Inserisci una quantità valida.");
    setBusy(true);
    try {
      await api.post(`/warehouse/${item.id}/movements`, { type, quantity: q, note });
      toast.success(type === "carico" ? "Carico registrato." : "Scarico registrato.");
      setQuantity(""); setNote("");
      await load();
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg rounded-xl border border-slate-700 bg-slate-900 p-6 shadow-2xl max-h-[90vh] overflow-auto" data-testid="movements-dialog">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-500 hover:text-slate-200"><X className="h-5 w-5" /></button>
        <h3 className="font-heading text-xl font-bold">{item.name}</h3>
        <p className="text-sm text-slate-500 font-mono-data">{item.codice_prodotto} · giacenza attuale {item.quantity ?? 0}</p>

        <form onSubmit={submit} className="mt-4 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <button type="button" data-testid="mov-type-carico" onClick={() => setType("carico")} className={`h-10 rounded-lg border text-sm flex items-center justify-center gap-1.5 ${type === "carico" ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-400" : "bg-slate-800 border-slate-700 text-slate-400"}`}><ArrowUp className="h-4 w-4" /> Carico</button>
            <button type="button" data-testid="mov-type-scarico" onClick={() => setType("scarico")} className={`h-10 rounded-lg border text-sm flex items-center justify-center gap-1.5 ${type === "scarico" ? "bg-rose-500/15 border-rose-500/40 text-rose-400" : "bg-slate-800 border-slate-700 text-slate-400"}`}><ArrowDown className="h-4 w-4" /> Scarico</button>
          </div>
          <div className="flex gap-2">
            <input type="number" min="1" data-testid="mov-quantity" className={inputCls} placeholder="Quantità" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
            <input data-testid="mov-note" className={inputCls} placeholder="Nota (opz.)" value={note} onChange={(e) => setNote(e.target.value)} />
            <button type="submit" disabled={busy} data-testid="mov-save" className="h-11 px-4 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-sm flex items-center gap-1">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}</button>
          </div>
        </form>

        <div className="mt-5">
          <div className="text-slate-500 text-xs uppercase tracking-wider mb-2">Storico movimenti</div>
          <div className="space-y-1.5 max-h-64 overflow-auto">
            {movements.length === 0 && <p className="text-slate-600 text-sm">Nessun movimento registrato.</p>}
            {movements.map((m) => (
              <div key={m.id} className="flex items-center gap-2 text-sm bg-slate-950/50 rounded-lg px-3 py-2">
                <span className={`h-6 w-6 rounded flex items-center justify-center ${m.type === "carico" ? "bg-emerald-500/15 text-emerald-400" : "bg-rose-500/15 text-rose-400"}`}>
                  {m.type === "carico" ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />}
                </span>
                <span className="font-mono-data">{m.type === "carico" ? "+" : "−"}{m.quantity}</span>
                <span className="text-slate-500 text-xs flex-1 truncate">{m.note}</span>
                <span className="text-slate-500 text-xs">→ {m.resulting_qty}</span>
                <span className="text-slate-600 text-[11px] font-mono-data">{(m.created_at || "").slice(0, 10)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Magazzino() {
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [movItem, setMovItem] = useState(null);

  const load = async () => {
    const { data } = await api.get("/warehouse");
    setItems(data);
    if (movItem) {
      const fresh = data.find((i) => i.id === movItem.id);
      if (fresh) setMovItem(fresh);
    }
    try { const s = await api.get("/suppliers"); setSuppliers(s.data); } catch (_) { setSuppliers([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.codice_azienda || !form.codice_prodotto || !form.ubicazione)
      return toast.error("Compila nome, codice azienda, codice prodotto e ubicazione.");
    setBusy(true);
    const payload = { ...form, quantity: Number(form.quantity) || 0, reorder_threshold: Number(form.reorder_threshold) || 0 };
    try {
      if (editId) { await api.put(`/warehouse/${editId}`, payload); toast.success("Componente aggiornato."); }
      else { await api.post("/warehouse", payload); toast.success("Componente aggiunto al magazzino."); }
      setForm(empty); setEditId(null); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const startEdit = (it) => {
    setEditId(it.id);
    setForm({
      name: it.name || "", codice_azienda: it.codice_azienda || "", codice_prodotto: it.codice_prodotto || "",
      ubicazione: it.ubicazione || "", quantity: it.quantity ?? "", reorder_threshold: it.reorder_threshold ?? "", supplier_id: it.supplier_id || "", notes: it.notes || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare questo componente dal magazzino?")) return;
    await api.delete(`/warehouse/${id}`);
    toast.success("Componente eliminato.");
    if (editId === id) { setEditId(null); setForm(empty); }
    load();
  };

  const filtered = useMemo(() => {
    if (!q) return items;
    const t = q.toLowerCase();
    return items.filter((i) => `${i.name} ${i.codice_azienda} ${i.codice_prodotto} ${i.ubicazione}`.toLowerCase().includes(t));
  }, [items, q]);

  const lowCount = items.filter((i) => i.low_stock).length;

  return (
    <div className="space-y-6 animate-fade-up">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Magazzino Componenti</h1>
        <p className="text-slate-500 text-sm mt-1">
          Componenti a magazzino, ubicazione, codici e giacenze.
          {lowCount > 0 && <span className="text-rose-400"> · {lowCount} sotto scorta minima</span>}
        </p>
      </div>

      <form onSubmit={submit} className="rounded-lg border border-slate-800 bg-slate-900/60 p-5 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-lg font-bold flex items-center gap-2"><Warehouse className="h-5 w-5 text-amber-400" /> {editId ? "Modifica componente" : "Nuovo componente"}</h2>
          {editId && <button type="button" onClick={() => { setEditId(null); setForm(empty); }} className="text-xs text-slate-400 hover:text-slate-200 flex items-center gap-1"><X className="h-3.5 w-3.5" /> Annulla modifica</button>}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <input data-testid="wh-name" className={inputCls} placeholder="Nome componente" value={form.name} onChange={(e) => set("name", e.target.value)} />
          <input data-testid="wh-codice-azienda" className={inputCls} placeholder="Codice azienda" value={form.codice_azienda} onChange={(e) => set("codice_azienda", e.target.value)} />
          <input data-testid="wh-codice-prodotto" className={inputCls} placeholder="Codice prodotto" value={form.codice_prodotto} onChange={(e) => set("codice_prodotto", e.target.value)} />
          <input data-testid="wh-ubicazione" className={inputCls} placeholder="Ubicazione (es. Scaffale A3)" value={form.ubicazione} onChange={(e) => set("ubicazione", e.target.value)} />
          <input data-testid="wh-quantity" type="number" min="0" className={inputCls} placeholder="Quantità iniziale" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} />
          <input data-testid="wh-reorder" type="number" min="0" className={inputCls} placeholder="Scorta minima" value={form.reorder_threshold} onChange={(e) => set("reorder_threshold", e.target.value)} />
          <select data-testid="wh-supplier" className={`${inputCls} sm:col-span-2`} value={form.supplier_id} onChange={(e) => set("supplier_id", e.target.value)}>
            <option value="">Nessun fornitore</option>
            {suppliers.map((s) => <option key={s.id} value={s.id}>{`${s.name} (${s.email})`}</option>)}
          </select>
          <input data-testid="wh-notes" className={`${inputCls} sm:col-span-2`} placeholder="Note (opzionale)" value={form.notes} onChange={(e) => set("notes", e.target.value)} />
        </div>
        <div className="mt-4">
          <button type="submit" disabled={busy} data-testid="wh-save" className="h-11 px-5 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-sm flex items-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {editId ? "Salva modifiche" : "Aggiungi a magazzino"}
          </button>
        </div>
      </form>

      <div className="relative max-w-md">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
        <input data-testid="wh-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome, codice o ubicazione…" className="w-full h-11 rounded-lg bg-slate-900 border border-slate-700 pl-10 pr-4 text-sm outline-none focus:border-amber-500" />
      </div>

      <div className="rounded-lg border border-slate-800 bg-slate-900/60 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="warehouse-table">
            <thead>
              <tr className="border-b border-slate-800 text-left text-xs font-mono-data uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Componente</th>
                <th className="px-4 py-3">Cod. Azienda</th>
                <th className="px-4 py-3">Cod. Prodotto</th>
                <th className="px-4 py-3">Ubicazione</th>
                <th className="px-4 py-3">Giacenza</th>
                <th className="px-4 py-3 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((i) => (
                <tr key={i.id} data-testid={`wh-row-${i.id}`} className="border-b border-slate-800/60 hover:bg-slate-800/40 transition-colors">
                  <td className="px-4 py-3 font-medium">{i.name}{i.supplier_name && <span className="block text-[11px] text-slate-500 font-normal">Fornitore: {i.supplier_name}</span>}</td>
                  <td className="px-4 py-3 font-mono-data text-slate-300">{i.codice_azienda}</td>
                  <td className="px-4 py-3 font-mono-data text-amber-400">{i.codice_prodotto}</td>
                  <td className="px-4 py-3"><span className="inline-flex items-center gap-1 text-slate-300"><MapPin className="h-3.5 w-3.5 text-slate-500" />{i.ubicazione}</span></td>
                  <td className="px-4 py-3">
                    <span className="font-mono-data">{i.quantity ?? 0}</span>
                    {i.reorder_threshold ? <span className="text-slate-600 text-xs"> / {i.reorder_threshold}</span> : null}
                    {i.low_stock && <span data-testid={`wh-low-${i.id}`} className="ml-2 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-rose-500/10 text-rose-400 border border-rose-500/30"><AlertTriangle className="h-3 w-3" /> riordino</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setMovItem(i)} data-testid={`wh-movements-${i.id}`} className="p-2 rounded-lg text-slate-400 hover:text-amber-400 hover:bg-slate-800" title="Movimenti"><ArrowDownUp className="h-4 w-4" /></button>
                      <button onClick={() => startEdit(i)} data-testid={`wh-edit-${i.id}`} className="p-2 rounded-lg text-slate-400 hover:text-amber-400 hover:bg-slate-800"><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => del(i.id)} data-testid={`wh-delete-${i.id}`} className="p-2 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-slate-800"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">Nessun componente in magazzino.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {movItem && <MovementsDialog item={movItem} onClose={() => setMovItem(null)} onChanged={load} />}
    </div>
  );
}
